import React, { useCallback, useMemo, useRef } from 'react';
import c from 'classnames';
import Ola from 'ola';
import { useTranslation } from 'react-i18next';

import { Lottie } from '@uncinc/uncinc-react-kitchen-sink';
import type { LottieItem } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Lottie/types';

import useAudioPlayer from '../../../AudioPlayers/AudioPlayer/hooks/useAudioPlayer';
import ButtonWrapper from '../../../ButtonWrapper';

import audio from '../../../../assets/audio/hoe-hoog-kom-jij.mp3';
import frequencyPlayerLottie from '../../../../assets/lotties/frequency-player.json';

import './style.scss';

const interpolatedFrame = Ola(0, 100);
const legendItems = ['7_khz', '10_khz', '14_khz', '16_khz'];

const LegendItem: React.FC<{ item: string }> = ({ item }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'components.webform.custom.frequency_player.legend' });

  return (
    <div className="WebformCustomFrequencyPlayer__LegendItem">
      <h3 className="WebformCustomFrequencyPlayer__LegendItem__title asH5">
        {t(`${item}.title`)}
      </h3>

      <span className="WebformCustomFrequencyPlayer__LegendItem__description body--small">
        {t(`${item}.description`)}
      </span>
    </div>
  );
};

const WebformCustomFrequencyPlayer: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'components.webform.custom.frequency_player' });

  const lottieRef = useRef<LottieItem>(null);
  const {
    audioRef,
    playing,
    setPlaying,
    hasEnded,
    setEnded,
    togglePlayback,
  } = useAudioPlayer();

  const classes = c('WebformCustomFrequencyPlayer');

  const [buttonLabel, buttonIcon] = useMemo(() => {
    if (hasEnded) {
      return ['replay', 'replay'];
    }

    if (playing === true) {
      return ['pause', 'pause'];
    }

    return ['play', 'play_arrow'];
  }, [hasEnded, playing]);

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current || !lottieRef.current) {
      return;
    }

    const { currentTime, duration } = audioRef.current;
    const progress = currentTime / duration;

    // Round the frame and pass it to Ola for frame interpolation.
    const rawFrame = Math.ceil(lottieRef.current.totalFrames * progress);
    interpolatedFrame.set(rawFrame);

    // Go to the frame we just calculated using the Ola value.
    lottieRef.current.goToAndStop(interpolatedFrame.value, true);
  }, [audioRef]);

  return (
    <div className={classes}>
      <div className="WebformCustomFrequencyPlayer__graph">
        <Lottie
          id="frequencyPlayerLottie"
          className="WebformCustomFrequencyPlayer__graph__lottie"
          ref={lottieRef}
          data={frequencyPlayerLottie}
          autoPlay={false}
          loop={false}
          startPosition={0}
        />

        <div className="WebformCustomFrequencyPlayer__graph__legend">
          <ul>
            {legendItems.map((item) => (
              <li key={`legend-item-${item}`}>
                <LegendItem item={item} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={audio}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => {
          setPlaying(true);

          if (hasEnded) {
            setEnded(false);
          }
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setEnded(true)}
      />

      <ButtonWrapper
        className="WebformCustomFrequencyPlayer__play-button"
        iconName={buttonIcon}
        onClick={togglePlayback}
      >
        {t(`play_button.${buttonLabel}`)}
      </ButtonWrapper>
    </div>
  );
};

export default WebformCustomFrequencyPlayer;
